/* -------------------------------------------------------------------------- */
/*                        Componente TimeRangeInput                           */
/* -------------------------------------------------------------------------- */
// Actualizado para usar RSuite DatePicker con solo tiempo

import { useContext, useEffect } from 'react';
import { Label, FormGroup, Row, Col } from 'reactstrap';
import moment from 'moment';
import { DatePicker } from 'rsuite';
import { FormattedMessage } from 'react-intl';
import { IntlContext } from '../../../utility/context/Internationalization';
import { XSquare } from 'react-feather';
import { createDateWithHoursAndMinutes } from 'utility/Utils';
import 'rsuite/dist/rsuite.min.css';

// Estilos personalizados
const customStyles = `
  .form-time-input-wrapper .rs-picker-default .rs-picker-toggle {
    border: 1px solid #d8d6de;
    border-radius: 0.357rem;
    padding: 0.571rem 0.857rem;
    font-size: 1rem;
    line-height: 1.45;
    background-color: #fff;
    color: #6e6b7b;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    height: 38px;
    width: 100%;
  }
  
  .form-time-input-wrapper .rs-picker-default .rs-picker-toggle:hover {
    border-color: #b3b1b7;
  }
  
  .form-time-input-wrapper .rs-picker-default.rs-picker-focused .rs-picker-toggle {
    border-color: #7367f0;
    box-shadow: 0 3px 10px 0 rgba(34, 41, 47, 0.1);
    outline: 0;
  }
`;

// Inyectar estilos
if (typeof document !== 'undefined' && !document.getElementById('form-time-input-styles')) {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.id = "form-time-input-styles";
  styleSheet.innerText = customStyles;
  document.head.appendChild(styleSheet);
}

interface Props {
  field: any;
  fieldstate: any;
  errorMessage?: any;
  required?: boolean;
  defaultHour?: number;
  defaultMinute?: number;
  maxTime?: any;
  minTime?: any;
  label?: any;
  disabled?: boolean;
  allowNulls?: boolean;
  inFormat?: boolean;
}

const TimeInput = ({
  field,
  fieldstate,
  required = false,
  maxTime,
  errorMessage,
  minTime,
  label,
  disabled,
  allowNulls = false,
  inFormat = false,
  defaultHour = 0,
  defaultMinute = 0
}: Props) => {
  const intlContext = useContext(IntlContext);

  /* Normaliza cuando viene como string HH:mm (modo edición) */
  useEffect(() => {
    if (inFormat && typeof field.value === 'string' && field.value) {
      field.onChange(moment(field.value, 'HH:mm').toDate());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (date: Date | null) => {
    if (date) {
      const formattedTime = inFormat 
        ? moment(date).format('HH:mm')
        : date;
      field.onChange(formattedTime);
    } else {
      field.onChange(null);
    }
  };

  // Convertir el valor actual a Date
  const getCurrentValue = () => {
    if (!field.value) return null;
    
    // Si es un string de tiempo como "14:30", crear una fecha de hoy con esa hora
    if (typeof field.value === 'string' && field.value.match(/^\d{2}:\d{2}$/)) {
      const [hours, minutes] = field.value.split(':');
      const today = new Date();
      today.setHours(parseInt(hours, 10));
      today.setMinutes(parseInt(minutes, 10));
      today.setSeconds(0);
      return today;
    }
    
    return new Date(field.value);
  };

  return (
    <FormGroup>
      <Label for={field.name}>
        {label || <FormattedMessage id="common.time" />}
        {required && <span className="required">&nbsp;*</span>}
      </Label>

      <Row>
        <Col sm={allowNulls ? '10' : undefined} className="form-time-input-wrapper">
          <DatePicker
            id={field.name}
            value={getCurrentValue()}
            onChange={handleChange}
            format="HH:mm"
            placeholder={intlContext?.locale === 'es' ? 'Seleccionar hora' : 'Select time'}
            disabled={disabled}
            showMeridian={false} // Usar formato 24 horas
            cleanable={!allowNulls}
            // Se voltea hacia arriba si no hay espacio abajo (evita que el panel
            // se corte al fondo del sliding pane / viewport) y se monta en body
            // para no quedar recortado por el overflow del contenedor.
            placement="autoVerticalStart"
            preventOverflow
            container={() => document.body}
            className={`rs-picker-default ${fieldstate.error ? 'rs-picker-error' : ''}`}
            locale={{
              hours: intlContext?.locale === 'es' ? 'Horas' : 'Hours',
              minutes: intlContext?.locale === 'es' ? 'Minutos' : 'Minutes',
              seconds: intlContext?.locale === 'es' ? 'Segundos' : 'Seconds'
            }}
          />
        </Col>

        {allowNulls && (
          <Col sm="1" className="mt-50 d-flex justify-content-center align-items-center" style={{ cursor: 'pointer' }}>
            <XSquare size={18} color="#ea5455" onClick={() => field.onChange(null)} />
          </Col>
        )}
      </Row>

      {fieldstate.error && (
        <div className="invalid-feedback" style={{ display: 'block' }}>
          {fieldstate.error?.message ||
            errorMessage ||
            <FormattedMessage id="common.requiredField" />}
        </div>
      )}
    </FormGroup>
  );
};

TimeInput.defaultProps = {
  required: false,
  disabled: false
};

export default TimeInput;